<template>
<div class="ss-root" :style="themePageStyle">
  <nav class="ss-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <back-button class="me-2" />
      <a href="javascript:void(0)" @click="$router.push('/dashboard')"><img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" /></a>
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">Slaughter Sheets <span v-if="farm.name" style="opacity:.7;font-weight:400;">· {{ farm.name }}</span></span>
    </div>
    <button class="btn btn-sm btn-warning" @click="openCreate" v-if="!editing"><i class="mdi mdi-plus me-1"></i>New sheet</button>
  </div></nav>

  <div class="container ss-container">
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

    <!-- LIST -->
    <div v-else-if="!editing">
      <div v-if="!sheets.length" class="ss-card text-center" style="padding:40px;">
        <i class="mdi mdi-clipboard-text-outline" style="font-size:48px;color:var(--fm-s,#6B6356);"></i>
        <div style="color:var(--fm-h,#F5F0E8);font-weight:600;margin-top:8px;">No slaughter sheets yet</div>
        <div style="color:var(--fm-s,#6B6356);font-size:12px;margin-top:4px;">Create one when a delivery note arrives at the abattoir.</div>
        <button class="btn btn-warning mt-3" @click="openCreate"><i class="mdi mdi-plus me-1"></i>New slaughter sheet</button>
      </div>
      <div v-else>
        <div v-for="s in sheets" :key="'ss-'+s.id" class="ss-card mb-2">
          <div class="d-flex justify-content-between align-items-start flex-wrap" style="gap:8px;">
            <div class="flex-fill" style="min-width:0;">
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <span class="ss-ref">{{ s.document_ref }}</span>
                <span class="ss-status" :class="'st-'+s.status">{{ statusLabel(s.status) }}</span>
              </div>
              <div style="color:var(--fm-h,#F5F0E8);font-weight:600;margin-top:4px;">{{ s.abattoir_name }}</div>
              <div style="color:var(--fm-s,#6B6356);font-size:12px;margin-top:2px;">
                <i class="mdi mdi-calendar me-1"></i>{{ fD(s.slaughter_date) }}
                · <i class="mdi mdi-cow me-1"></i>{{ s.items_count }} animal{{ s.items_count===1?'':'s' }}
                <span v-if="s.delivery_note_ref"> · DN <strong>{{ s.delivery_note_ref }}</strong></span>
              </div>
              <div v-if="s.animals_passed != null" style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:2px;">Passed {{ s.animals_passed }} · Condemned {{ s.animals_condemned || 0 }}</div>
            </div>
            <div class="d-flex gap-1 flex-wrap">
              <button class="btn btn-sm btn-warning" @click="openPrint(s)"><i class="mdi mdi-printer me-1"></i>View / Print</button>
              <button class="btn btn-sm btn-outline-warning" @click="openEdit(s)"><i class="mdi mdi-pencil me-1"></i>Edit</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CREATE WIZARD -->
    <div v-else-if="editing && !current">
      <div class="ss-card">
        <h5 class="fw-bold" style="color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-plus-box me-1"></i>New slaughter sheet</h5>
        <p style="color:var(--fm-s,#6B6356);font-size:12px;">Pick a dispatched delivery note to seed the animals, or skip and add manually.</p>
        <div class="mb-2"><label class="ss-label">Delivery note (optional)</label>
          <select v-model.number="newSheet.deliveryNoteId" class="ss-input w-100">
            <option :value="0">— None / manual —</option>
            <option v-for="dn in dispatchedNotes" :key="'dn-'+dn.id" :value="dn.id">{{ dn.document_ref }} · {{ dn.destination_name }} · {{ dn.total_animals }} animals · {{ fD(dn.delivery_date) }}</option>
          </select>
        </div>
        <div class="row g-2 mb-2">
          <div class="col-md-7"><label class="ss-label">Abattoir name *</label><input v-model="newSheet.abattoirName" class="ss-input w-100" placeholder="e.g. Rumuruti Slaughterhouse" /></div>
          <div class="col-md-5"><label class="ss-label">Slaughter date *</label><input v-model="newSheet.slaughterDate" type="date" class="ss-input w-100" /></div>
        </div>
        <button class="btn btn-warning w-100" @click="createSheet" :disabled="!canCreate || creating">
          <span v-if="creating"><span class="spinner-border spinner-border-sm me-1"></span>Creating...</span>
          <span v-else><i class="mdi mdi-content-save me-1"></i>Create &amp; open</span>
        </button>
        <button class="btn btn-sm btn-link text-muted w-100 mt-1" @click="cancelEdit">Cancel</button>
      </div>
    </div>

    <!-- EDIT (tabbed) -->
    <div v-else-if="current">
      <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap" style="gap:8px;">
        <div>
          <span class="ss-ref">{{ current.document_ref }}</span>
          <span class="ss-status ms-2" :class="'st-'+current.status">{{ statusLabel(current.status) }}</span>
          <div style="color:var(--fm-s,#6B6356);font-size:12px;margin-top:4px;">{{ current.abattoir_name }} · {{ fD(current.slaughter_date) }} · {{ current.items.length }} animals</div>
        </div>
        <div class="d-flex gap-1">
          <button class="btn btn-sm btn-outline-warning" @click="openPrint(current)"><i class="mdi mdi-printer me-1"></i>Preview / Print</button>
          <button class="btn btn-sm btn-outline-secondary" @click="cancelEdit"><i class="mdi mdi-close"></i></button>
        </div>
      </div>

      <div class="ss-subtabs">
        <button class="ss-subtab" :class="{active: sub==='arrival'}" @click="sub='arrival'">1. Arrival &amp; Ante-mortem</button>
        <button class="ss-subtab" :class="{active: sub==='carcass'}" @click="sub='carcass'">2. Carcass</button>
        <button class="ss-subtab" :class="{active: sub==='offal'}" @click="sub='offal'">3. Offal &amp; PM</button>
        <button class="ss-subtab" :class="{active: sub==='signoff'}" @click="sub='signoff'">4. Sign-off</button>
      </div>

      <!-- TAB 1 -->
      <div v-if="sub==='arrival'" class="ss-card">
        <h6 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">A. Consignment arrival</h6>
        <div class="row g-2 mb-2">
          <div class="col-md-4"><label class="ss-label">Abattoir name</label><input v-model="current.abattoir_name" class="ss-input w-100" /></div>
          <div class="col-md-4"><label class="ss-label">Abattoir Reg. No.</label><input v-model="current.abattoir_reg_no" class="ss-input w-100" /></div>
          <div class="col-md-4"><label class="ss-label">Abattoir Licence No.</label><input v-model="current.abattoir_licence_no" class="ss-input w-100" /></div>
        </div>
        <div class="row g-2 mb-2">
          <div class="col-md-4"><label class="ss-label">Slaughter date</label><input v-model="current.slaughter_date" type="date" class="ss-input w-100" /></div>
          <div class="col-md-4"><label class="ss-label">Arrival date &amp; time</label><input v-model="current.arrival_at" type="datetime-local" class="ss-input w-100" /></div>
          <div class="col-md-4"><label class="ss-label">Lairage hours</label><input v-model.number="current.lairage_hours" type="number" step="0.5" class="ss-input w-100" /></div>
        </div>
        <div class="row g-2 mb-2">
          <div class="col-md-4"><label class="ss-label">Transporter</label><input v-model="current.transporter_name" class="ss-input w-100" /></div>
          <div class="col-md-4"><label class="ss-label">Vehicle reg</label><input v-model="current.vehicle_reg" class="ss-input w-100" /></div>
          <div class="col-md-4"><label class="ss-label">DVS movement permit</label><input v-model="current.dvs_permit_no" class="ss-input w-100" /></div>
        </div>
        <div class="row g-2 mb-3">
          <div class="col-md-6"><label class="ss-label">Vet officer</label><input v-model="current.vet_officer_name" class="ss-input w-100" /></div>
          <div class="col-md-6"><label class="ss-label">Vet DVS / reg no.</label><input v-model="current.vet_dvs_no" class="ss-input w-100" /></div>
        </div>

        <h6 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">B. Ante-mortem register</h6>
        <div class="table-responsive">
          <table class="ss-table"><thead><tr>
            <th>#</th><th>Ear Tag</th><th>Class</th><th>Breed</th><th>Age (mo)</th><th>LW (kg)</th><th>AM Status</th><th>Slaughter time</th><th v-if="current.status!=='signed_off'"></th>
          </tr></thead><tbody>
            <tr v-for="(it, idx) in current.items" :key="'am-'+it.id">
              <td>{{ idx+1 }}</td>
              <td><input v-model="it.ear_tag" class="ss-input" style="width:90px;" /></td>
              <td><input v-model="it.animal_class" class="ss-input" style="width:80px;" /></td>
              <td><input v-model="it.breed_type" class="ss-input" style="width:90px;" /></td>
              <td><input v-model.number="it.est_age_months" type="number" class="ss-input" style="width:60px;" /></td>
              <td><input v-model.number="it.liveweight_kg" type="number" step="0.1" class="ss-input" style="width:75px;" /></td>
              <td>
                <select v-model="it.ante_mortem_status" class="ss-input" style="width:90px;">
                  <option value="pending">Pending</option>
                  <option value="pass">PASS</option>
                  <option value="hold">HOLD</option>
                  <option value="reject">REJECT</option>
                </select>
              </td>
              <td><input v-model="it.slaughter_time" type="time" class="ss-input" style="width:100px;" /></td>
              <td v-if="current.status!=='signed_off'"><button class="btn btn-sm btn-outline-danger" style="padding:1px 7px;" title="Remove animal" @click="removeItem(it)"><i class="mdi mdi-close"></i></button></td>
            </tr>
            <tr v-if="!current.items.length"><td colspan="9" style="text-align:center;color:var(--fm-s,#6B6356);padding:14px;">No animals on this sheet yet — add them below.</td></tr>
          </tbody></table>
        </div>
        <button v-if="current.status!=='signed_off'" class="btn btn-sm btn-outline-warning mb-3" :disabled="addingItem" @click="addItem"><i class="mdi mdi-plus me-1"></i>Add animal</button>
        <div class="mb-3"><label class="ss-label">Ante-mortem remarks</label><textarea v-model="current.ante_mortem_remarks" class="ss-input w-100" rows="2"></textarea></div>
        <button class="btn btn-warning" @click="saveAll" :disabled="saving"><i class="mdi mdi-content-save me-1"></i>Save section</button>
      </div>

      <!-- TAB 2 -->
      <div v-if="sub==='carcass'" class="ss-card">
        <h6 class="fw-bold mb-2" style="color:var(--fm-h,#F5F0E8);">Carcass weights &amp; grading</h6>
        <div class="table-responsive">
          <table class="ss-table"><thead><tr>
            <th>#</th><th>Ear Tag</th><th>Age (mo)</th><th>LW (kg)</th><th>Hot CW</th><th>Cold CW</th><th>Dress %</th><th>Side L</th><th>Side R</th><th>Fat 1-5</th><th>Muscle 1-5</th><th>Bruise (site &amp; size, cm)</th><th>Trim (kg)</th><th>Grade</th>
          </tr></thead><tbody>
            <tr v-for="(it, idx) in current.items" :key="'cc-'+it.id">
              <td>{{ idx+1 }}</td>
              <td>{{ it.ear_tag }}</td>
              <td><input v-model.number="it.est_age_months" type="number" min="0" class="ss-input" style="width:60px;" /></td>
              <td>{{ it.liveweight_kg || '—' }}</td>
              <td><input v-model.number="it.hot_cw_kg" type="number" step="0.1" class="ss-input" style="width:75px;" @blur="recalcDress(it)" /></td>
              <td><input v-model.number="it.cold_cw_kg" type="number" step="0.1" class="ss-input" style="width:75px;" @blur="recalcDress(it)" /></td>
              <td style="min-width:70px;color:var(--fm-h,#F5F0E8);font-weight:600;">{{ it.dress_pct != null && it.dress_pct !== '' ? it.dress_pct + '%' : '—' }}</td>
              <td><input v-model.number="it.side_l_kg" type="number" step="0.1" class="ss-input" style="width:75px;" /></td>
              <td><input v-model.number="it.side_r_kg" type="number" step="0.1" class="ss-input" style="width:75px;" /></td>
              <td><input v-model.number="it.fat_score" type="number" min="1" max="5" class="ss-input" style="width:55px;" /></td>
              <td><input v-model.number="it.muscle_score" type="number" min="1" max="5" class="ss-input" style="width:55px;" /></td>
              <td><input v-model="it.bruise_location" class="ss-input" style="width:130px;" placeholder="e.g. rump, 4 cm" /></td>
              <td><input v-model.number="it.trim_loss_kg" type="number" step="0.1" class="ss-input" style="width:65px;" /></td>
              <td style="min-width:55px;color:var(--fm-h,#F5F0E8);font-weight:700;">{{ gradeFor(it) || '—' }}</td>
            </tr>
          </tbody><tfoot><tr style="font-weight:700;color:var(--fm-h,#F5F0E8);">
            <td colspan="3">TOTALS</td>
            <td>{{ sumOf('liveweight_kg') }}</td>
            <td>{{ sumOf('hot_cw_kg') }}</td>
            <td>{{ sumOf('cold_cw_kg') }}</td>
            <td>{{ avgOf('dress_pct') }}%</td>
            <td>{{ sumOf('side_l_kg') }}</td>
            <td>{{ sumOf('side_r_kg') }}</td>
            <td colspan="3"></td>
            <td>{{ sumOf('trim_loss_kg') }}</td>
            <td></td>
          </tr></tfoot></table>
        </div>
        <div class="ss-grade-key">
          <strong>Grade key:</strong> A Prime · B Choice · C Standard · D Utility — graded automatically from age, fat cover, muscling &amp; dressing %
        </div>
        <button class="btn btn-warning mt-2" @click="saveAll" :disabled="saving"><i class="mdi mdi-content-save me-1"></i>Save section</button>
      </div>

      <!-- TAB 3 -->
      <div v-if="sub==='offal'" class="ss-card">
        <h6 class="fw-bold mb-2" style="color:var(--fm-h,#F5F0E8);">Offal weights (kg)</h6>
        <div class="table-responsive">
          <table class="ss-table"><thead><tr>
            <th>#</th><th>Ear Tag</th><th>Hide</th><th>Liver</th><th>Tripe</th><th>Heart</th><th>Lungs</th><th>Kidneys</th><th>Head</th><th>Legs</th><th>Blood (L)</th><th>Other</th><th>Total</th>
          </tr></thead><tbody>
            <tr v-for="(it, idx) in current.items" :key="'of-'+it.id">
              <td>{{ idx+1 }}</td>
              <td>{{ it.ear_tag }}</td>
              <td><input v-model.number="it.hide_kg" type="number" step="0.1" class="ss-input" style="width:65px;" @blur="recalcOffal(it)" /></td>
              <td><input v-model.number="it.liver_kg" type="number" step="0.1" class="ss-input" style="width:65px;" @blur="recalcOffal(it)" /></td>
              <td><input v-model.number="it.tripe_kg" type="number" step="0.1" class="ss-input" style="width:65px;" @blur="recalcOffal(it)" /></td>
              <td><input v-model.number="it.heart_kg" type="number" step="0.1" class="ss-input" style="width:65px;" @blur="recalcOffal(it)" /></td>
              <td><input v-model.number="it.lungs_kg" type="number" step="0.1" class="ss-input" style="width:65px;" @blur="recalcOffal(it)" /></td>
              <td><input v-model.number="it.kidneys_kg" type="number" step="0.1" class="ss-input" style="width:65px;" @blur="recalcOffal(it)" /></td>
              <td><input v-model.number="it.head_kg" type="number" step="0.1" class="ss-input" style="width:65px;" @blur="recalcOffal(it)" /></td>
              <td><input v-model.number="it.legs_kg" type="number" step="0.1" class="ss-input" style="width:65px;" @blur="recalcOffal(it)" /></td>
              <td><input v-model.number="it.blood_l" type="number" step="0.1" class="ss-input" style="width:65px;" /></td>
              <td><input v-model.number="it.other_offal_kg" type="number" step="0.1" class="ss-input" style="width:65px;" @blur="recalcOffal(it)" /></td>
              <td>{{ it.total_offal_kg || '—' }}</td>
            </tr>
          </tbody></table>
        </div>

        <h6 class="fw-bold mt-3" style="color:var(--fm-h,#F5F0E8);">Post-mortem findings</h6>
        <div class="table-responsive">
          <table class="ss-table"><thead><tr>
            <th>#</th><th>Ear Tag</th><th>Liver</th><th>Lungs</th><th>Lymph nodes</th><th>Condemned parts</th><th>Reason</th><th>Vet initials</th><th>PM Status</th>
          </tr></thead><tbody>
            <tr v-for="(it, idx) in current.items" :key="'pm-'+it.id">
              <td>{{ idx+1 }}</td>
              <td>{{ it.ear_tag }}</td>
              <td><input v-model="it.liver_condition" class="ss-input" style="width:95px;" placeholder="Normal" /></td>
              <td><input v-model="it.lungs_condition" class="ss-input" style="width:95px;" placeholder="Normal" /></td>
              <td><input v-model="it.lymph_nodes_condition" class="ss-input" style="width:95px;" placeholder="Normal" /></td>
              <td><input v-model="it.condemned_parts" class="ss-input" style="width:130px;" placeholder="NONE" /></td>
              <td><input v-model="it.condemn_reason" class="ss-input" style="width:130px;" /></td>
              <td><input v-model="it.vet_initials" class="ss-input" style="width:65px;" /></td>
              <td>
                <select v-model="it.pm_status" class="ss-input" style="width:90px;">
                  <option value="pending">Pending</option>
                  <option value="passed">PASSED</option>
                  <option value="partial">Partial</option>
                  <option value="failed">FAILED</option>
                </select>
              </td>
            </tr>
          </tbody></table>
        </div>
        <div class="mb-3 mt-3"><label class="ss-label">PM remarks</label><textarea v-model="current.pm_remarks" class="ss-input w-100" rows="2"></textarea></div>
        <button class="btn btn-warning" @click="saveAll" :disabled="saving"><i class="mdi mdi-content-save me-1"></i>Save section</button>
      </div>

      <!-- TAB 4 -->
      <div v-if="sub==='signoff'" class="ss-card">
        <h6 class="fw-bold mb-2" style="color:var(--fm-h,#F5F0E8);">A. Cap. 356 inspection certificate</h6>
        <div class="row g-2 mb-2">
          <div class="col-md-6"><label class="ss-label">Meat inspector name</label><input v-model="current.meat_inspector_name" class="ss-input w-100" /></div>
          <div class="col-md-6"><label class="ss-label">Inspector reg. no.</label><input v-model="current.meat_inspector_reg_no" class="ss-input w-100" /></div>
        </div>
        <div class="row g-2 mb-2">
          <div class="col-md-4"><label class="ss-label">Animals passed</label><input v-model.number="current.animals_passed" type="number" class="ss-input w-100" :placeholder="autoTallyPassed" /></div>
          <div class="col-md-4"><label class="ss-label">Animals condemned</label><input v-model.number="current.animals_condemned" type="number" class="ss-input w-100" :placeholder="autoTallyCondemned" /></div>
          <div class="col-md-4"><label class="ss-label">Weight condemned (kg)</label><input v-model.number="current.total_weight_condemned_kg" type="number" step="0.1" class="ss-input w-100" />
            <div v-if="autoCondemnedWeight > 0" style="font-size:11px;color:var(--fm-s,#6B6356);margin-top:2px;">Auto from failed PM animals: {{ autoCondemnedWeight }} kg
              <a v-if="Number(current.total_weight_condemned_kg) !== autoCondemnedWeight" href="javascript:void(0)" @click="current.total_weight_condemned_kg = autoCondemnedWeight">apply</a>
            </div>
          </div>
        </div>
        <div class="row g-2 mb-3">
          <div class="col-md-6">
            <label class="ss-label">Notifiable disease detected?</label>
            <select v-model="current.notifiable_disease" class="ss-input w-100">
              <option :value="0">NO</option>
              <option :value="1">YES — see remarks</option>
            </select>
          </div>
          <div class="col-md-6"><label class="ss-label">DVS notification ref</label><input v-model="current.dvs_notification_ref" class="ss-input w-100" /></div>
        </div>

        <h6 class="fw-bold mb-2" style="color:var(--fm-h,#F5F0E8);">B. Signatures</h6>
        <div class="row g-2 mb-3">
          <div class="col-md-6"><label class="ss-label">Ranch representative</label><input v-model="current.ranch_signed_name" class="ss-input w-100" /></div>
          <div class="col-md-6"><label class="ss-label">Abattoir manager</label><input v-model="current.abattoir_signed_name" class="ss-input w-100" /></div>
          <div class="col-md-6"><label class="ss-label">Meat inspector / vet</label><input v-model="current.inspector_signed_name" class="ss-input w-100" /></div>
          <div class="col-md-6"><label class="ss-label">Buyer / off-taker</label><input v-model="current.buyer_signed_name" class="ss-input w-100" /></div>
        </div>

        <div class="d-flex gap-2 flex-wrap">
          <button class="btn btn-warning" @click="saveAll" :disabled="saving"><i class="mdi mdi-content-save me-1"></i>Save changes</button>
          <button class="btn btn-success" @click="signOff" :disabled="saving"><i class="mdi mdi-check-decagram me-1"></i>Sign off &amp; lock</button>
          <button class="btn btn-outline-warning" @click="openInvoiceModal" :disabled="saving || !invoiceableItems.length"><i class="mdi mdi-receipt-text-outline me-1"></i>Generate Invoice for Corporate Client</button>
        </div>
        <div v-if="!invoiceableItems.length" style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:6px;">
          <i class="mdi mdi-information-outline"></i> Capture carcass weights on at least one item before you can invoice.
        </div>
      </div>
    </div>
  </div>

  <!-- ===== GENERATE INVOICE MODAL ===== -->
  <div v-if="invoiceModalOpen" class="ss-modal-bg" @click.self="closeInvoiceModal">
    <div class="ss-modal">
      <div class="ss-modal-head">
        <h5>Generate Invoice — {{ current.document_ref || 'this slaughter sheet' }}</h5>
        <button class="ss-close" @click="closeInvoiceModal"><i class="mdi mdi-close"></i></button>
      </div>

      <div v-if="!corporateClients.length && !loadingClients" class="ss-empty">
        <i class="mdi mdi-domain-off"></i>
        <p>You don't have any corporate clients yet. Add one first to invoice them.</p>
        <button class="btn btn-warning" @click="$router.push('/my-corporate-clients')">Add a corporate client</button>
      </div>

      <template v-else>
        <div class="ss-field">
          <label>Corporate client</label>
          <select v-model="selectedClientId" class="ss-input w-100" @change="loadPreview">
            <option value="">— Pick a client —</option>
            <option v-for="c in corporateClients" :key="c.id" :value="c.id">
              {{ c.company_name }} · {{ c.payment_terms_days }}d {{ formatMethod(c.payment_method) }} · {{ c.billing_weight_basis }} weight
            </option>
          </select>
        </div>

        <div v-if="selectedClient" class="ss-client-snap">
          <span><strong>Billing basis:</strong> {{ basisLabel(selectedClient.billing_weight_basis) }}</span>
          <span><strong>Pricing:</strong> {{ selectedClient.pricing_mode === 'by_grade' ? 'Grade-based' : ('Flat KES ' + Number(selectedClient.flat_rate_per_kg||0).toLocaleString() + '/kg') }}</span>
          <span><strong>Terms:</strong> {{ selectedClient.payment_terms_days }}-day {{ formatMethod(selectedClient.payment_method) }}</span>
          <span><strong>Condemned:</strong> {{ selectedClient.condemnation_billing === 'net_of_condemned' ? 'Deducted' : 'Billed in full' }}</span>
        </div>

        <div v-if="selectedClient && selectedClient.purchase_order_required" class="ss-field">
          <label>Purchase Order Number (required for this client)</label>
          <input v-model="poNumber" class="ss-input w-100" placeholder="e.g. PO-2026-0042">
        </div>

        <div v-if="loadingPreview" class="ss-loading"><i class="mdi mdi-loading mdi-spin"></i> Calculating…</div>

        <div v-else-if="invoicePreview" class="ss-preview">
          <h6>Items on this invoice ({{ invoicePreview.lines.length }})</h6>
          <table class="ss-preview-tbl">
            <thead><tr><th>Ear Tag</th><th>Grade</th><th class="num">Weight</th><th class="num">Rate</th><th class="num">Amount</th></tr></thead>
            <tbody>
              <tr v-for="(ln, i) in invoicePreview.lines" :key="i">
                <td>{{ ln.ear_tag || '—' }}</td>
                <td>{{ ln.grade || '—' }}</td>
                <td class="num">{{ Number(ln.weight_kg).toFixed(2) }} kg<span v-if="Number(ln.condemned_weight_kg)>0" class="ss-cond" :title="Number(ln.condemned_weight_kg).toFixed(2) + ' kg condemned'">*</span></td>
                <td class="num">{{ Number(ln.rate_per_kg).toLocaleString() }}</td>
                <td class="num">{{ Number(ln.amount_kes).toLocaleString() }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr><td colspan="4" class="num"><strong>Subtotal</strong></td><td class="num"><strong>{{ Number(invoicePreview.subtotal).toLocaleString() }}</strong></td></tr>
              <tr v-if="invoicePreview.vat > 0"><td colspan="4" class="num">VAT</td><td class="num">{{ Number(invoicePreview.vat).toLocaleString() }}</td></tr>
              <tr v-if="invoicePreview.wht > 0"><td colspan="4" class="num">WHT</td><td class="num">−{{ Number(invoicePreview.wht).toLocaleString() }}</td></tr>
              <tr class="total-row"><td colspan="4" class="num"><strong>TOTAL {{ invoicePreview.currency }}</strong></td><td class="num"><strong>{{ Number(invoicePreview.total).toLocaleString() }}</strong></td></tr>
            </tfoot>
          </table>
        </div>

        <div class="ss-field">
          <label>Notes (optional, shown on invoice)</label>
          <textarea v-model="invoiceNotes" rows="2" class="ss-input w-100"></textarea>
        </div>

        <div class="ss-modal-foot">
          <button class="btn btn-link" @click="closeInvoiceModal">Cancel</button>
          <button class="btn btn-warning" :disabled="!selectedClientId || generating || (selectedClient && selectedClient.purchase_order_required && !poNumber)" @click="confirmGenerate">
            <span v-if="generating"><i class="mdi mdi-loading mdi-spin me-1"></i>Generating &amp; sending…</span>
            <span v-else><i class="mdi mdi-send me-1"></i>Issue invoice &amp; notify {{ selectedClient ? selectedClient.company_name : 'client' }}</span>
          </button>
        </div>
      </template>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] .ss-root{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .ss-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .ss-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .ss-input{background:#F7F5F2!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .ss-table th{background:#F0EBE4!important;color:#1A1A1A!important}
</style>

<style scoped>
.ss-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.ss-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.ss-container{padding-top:80px;padding-bottom:60px;max-width:1240px}
.ss-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:14px}
.ss-ref{font-family:monospace;color:#BFA164;font-weight:700;font-size:13px;background:rgba(191,161,100,.1);padding:3px 8px;border-radius:6px}
.ss-status{font-size:10px;font-weight:700;padding:3px 10px;border-radius:20px;text-transform:uppercase;letter-spacing:.4px}
.ss-status.st-draft{background:rgba(160,152,136,.16);color:#A09888}
.ss-status.st-ante_mortem_complete{background:rgba(102,178,255,.16);color:#66B2FF}
.ss-status.st-slaughtered{background:rgba(241,180,76,.16);color:#f1b44c}
.ss-status.st-post_mortem_complete{background:rgba(191,161,100,.16);color:#BFA164}
.ss-status.st-signed_off{background:rgba(80,200,120,.16);color:#50C878}
.ss-label{display:block;font-size:11px;color:var(--fm-s);text-transform:uppercase;letter-spacing:.3px;margin-bottom:4px;font-weight:600}
.ss-input{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#2A2520);border-radius:6px;padding:6px 8px;color:var(--fm-h,#F5F0E8);font-size:12px}
.ss-input:focus{outline:none;border-color:#BFA164}
.ss-subtabs{display:flex;gap:4px;border-bottom:1px solid var(--fm-br);margin-bottom:10px;flex-wrap:wrap}
.ss-subtab{background:transparent;border:none;color:var(--fm-m);font-size:13px;font-weight:600;padding:8px 12px;cursor:pointer;border-radius:6px 6px 0 0}
.ss-subtab.active{color:#BFA164;background:rgba(191,161,100,.08);border-bottom:2px solid #BFA164}
.ss-table{width:100%;border-collapse:collapse;font-size:12px}
.ss-table th{background:var(--fm-bg2,#0F0F0F);color:var(--fm-s);font-size:11px;letter-spacing:.4px;padding:8px 6px;text-align:left;text-transform:uppercase;font-weight:700}
.ss-table td{padding:5px 4px;border-bottom:1px solid var(--fm-br,#1E1E1E);vertical-align:middle}
.ss-grade-key{margin-top:8px;padding:6px 12px;background:rgba(191,161,100,.08);border-left:3px solid #BFA164;border-radius:0 6px 6px 0;font-size:11px;color:var(--fm-m)}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
export default {
  name: "SlaughterSheets",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,
    loading:true, saving:false, creating:false, addingItem:false,
    farm:{}, sheets:[], dispatchedNotes:[],
    editing:false, current:null, sub:'arrival',
    newSheet:{ deliveryNoteId:0, abattoirName:'', slaughterDate:new Date().toISOString().slice(0,10) },
    // Generate-invoice modal
    invoiceModalOpen:false, loadingClients:false, corporateClients:[],
    selectedClientId:'', loadingPreview:false, invoicePreview:null,
    poNumber:'', invoiceNotes:'', generating:false
  }},
  computed:{
    themePageStyle(){ return this.darkMode ? 'background:#0A0A0A;min-height:100vh;color:#F5F0E8;' : 'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'; },
    farmId(){ return this.$route.params.farmId; },
    canCreate(){ return this.newSheet.abattoirName && this.newSheet.slaughterDate; },
    autoTallyPassed(){ return this.current ? this.current.items.filter(function(i){return i.pm_status==='passed'}).length : 0; },
    autoTallyCondemned(){ return this.current ? this.current.items.filter(function(i){return i.pm_status==='failed'||i.pm_status==='partial'}).length : 0; },
    autoCondemnedWeight(){
      if (!this.current) return 0;
      var s = 0;
      this.current.items.forEach(function(i){
        if (i.pm_status === 'failed') s += Number(i.cold_cw_kg) || Number(i.hot_cw_kg) || Number(i.liveweight_kg) || 0;
      });
      return Math.round(s * 100) / 100;
    },
    invoiceableItems(){
      if (!this.current || !this.current.items) return [];
      return this.current.items.filter(function(it){
        return !it.corporate_invoice_line_id && (Number(it.hot_cw_kg)>0 || Number(it.cold_cw_kg)>0 || Number(it.liveweight_kg)>0);
      });
    },
    selectedClient(){
      var id = this.selectedClientId; if(!id) return null;
      return this.corporateClients.find(function(c){ return c.id === id; }) || null;
    }
  },
  watch:{
    sub(v){
      // Landing on Sign-off: pull condemned weight from failed-PM animals if not filled in yet
      if (v === 'signoff' && this.current && this.autoCondemnedWeight > 0
          && (this.current.total_weight_condemned_kg == null || this.current.total_weight_condemned_kg === '' || Number(this.current.total_weight_condemned_kg) === 0)) {
        this.current.total_weight_condemned_kg = this.autoCondemnedWeight;
      }
    }
  },
  mounted(){
    this._applyThemeOnce();var self=this;
    this.themeObserver=new MutationObserver(function(){try{self._applyThemeOnce();}catch(e){}});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    if(!sessionStorage.getItem('userId')){this.$router.push('/login');return}
    this.loadAll();
  },
  beforeUnmount(){ if(this.themeObserver){this.themeObserver.disconnect()} },
  methods:{
    _applyThemeOnce(){var current=document.documentElement.getAttribute('data-theme');var desired=(localStorage.getItem('flockr_theme')||current||'dark');if(current!==desired)document.documentElement.setAttribute('data-theme',desired);this.darkMode=desired!=='light';},
    fD(d){ if(!d)return''; try{ return new Date(d).toLocaleDateString(undefined,{day:'2-digit',month:'short',year:'numeric'}) }catch(e){return d} },
    statusLabel(s){ return ({draft:'Draft',ante_mortem_complete:'AM Done',slaughtered:'Slaughtered',post_mortem_complete:'PM Done',signed_off:'Signed Off'})[s] || s; },
    async loadAll(){
      this.loading = true;
      try {
        var f = await executeJson({ function:'getFarmDetail', farmId:this.farmId }, flocker);
        if (f.data && f.data.success) this.farm = (f.data.data && f.data.data.farm) || {};
        var s = await executeJson({ function:'listSlaughterSheets', farmId:this.farmId }, flocker);
        if (s.data && s.data.success) this.sheets = (s.data.data && s.data.data.slaughterSheets) || [];
        var d = await executeJson({ function:'listDeliveryNotes', farmId:this.farmId }, flocker);
        if (d.data && d.data.success) {
          this.dispatchedNotes = ((d.data.data && d.data.data.deliveryNotes) || []).filter(function(x){ return x.status === 'dispatched' || x.status === 'received'; });
        }
      } catch(e){ console.error(e); }
      this.loading = false;
    },
    openCreate(){
      this.editing = true;
      this.current = null;
      this.newSheet = { deliveryNoteId:0, abattoirName:'', slaughterDate:new Date().toISOString().slice(0,10) };
    },
    cancelEdit(){ this.editing = false; this.current = null; this.loadAll(); },
    async createSheet(){
      if (!this.canCreate) return;
      this.creating = true;
      try {
        var r = await executeJson({ function:'createSlaughterSheet', farmId:this.farmId, userId:sessionStorage.getItem('userId'),
          deliveryNoteId: this.newSheet.deliveryNoteId || 0, abattoirName:this.newSheet.abattoirName, slaughterDate:this.newSheet.slaughterDate }, flocker);
        if (r.data && r.data.success) {
          await this.openEdit({ id: r.data.data.slaughterSheetId });
        } else alert((r.data && r.data.message) || 'Failed');
      } catch(e){ alert('Network error'); }
      this.creating = false;
    },
    async openEdit(s){
      try {
        var r = await executeJson({ function:'getSlaughterSheet', slaughterSheetId:s.id }, flocker);
        if (r.data && r.data.success) {
          var ss = r.data.data.slaughterSheet;
          // Normalize fields
          ss.items = (ss.items || []).map(function(it){
            var clone = Object.assign({}, it);
            ['liveweight_kg','hot_cw_kg','cold_cw_kg','dress_pct','side_l_kg','side_r_kg','fat_score','muscle_score','trim_loss_kg',
             'hide_kg','liver_kg','tripe_kg','heart_kg','lungs_kg','kidneys_kg','head_kg','legs_kg','blood_l','other_offal_kg','total_offal_kg',
             'price_per_kg_kes','amount_kes','est_age_months'].forEach(function(f){
              if (clone[f] !== null && clone[f] !== undefined && clone[f] !== '') clone[f] = Number(clone[f]); else clone[f] = null;
            });
            return clone;
          });
          this.current = ss;
          this.editing = true;
          this.sub = 'arrival';
        }
      } catch(e){ alert('Could not load.'); }
    },
    async addItem(){
      if (!this.current) return;
      this.addingItem = true;
      try {
        var r = await executeJson({ function:'addSlaughterItem', slaughterSheetId: this.current.id }, flocker);
        if (r.data && r.data.success) {
          this.current.items.push({ id: r.data.data.itemId, ear_tag:'', animal_class:null, breed_type:null, est_age_months:null,
            liveweight_kg:null, ante_mortem_status:'pending', slaughter_time:null, hot_cw_kg:null, cold_cw_kg:null, dress_pct:null,
            side_l_kg:null, side_r_kg:null, grade:null, fat_score:null, muscle_score:null, bruise_location:null, trim_loss_kg:null,
            hide_kg:null, liver_kg:null, tripe_kg:null, heart_kg:null, lungs_kg:null, kidneys_kg:null, head_kg:null, legs_kg:null,
            blood_l:null, other_offal_kg:null, total_offal_kg:null, pm_status:'pending', liver_condition:null, lungs_condition:null,
            lymph_nodes_condition:null, condemned_parts:null, condemn_reason:null, vet_initials:null,
            price_per_kg_kes:null, amount_kes:null, remarks:null });
        } else alert((r.data && r.data.message) || 'Failed to add animal');
      } catch(e){ alert('Network error'); }
      this.addingItem = false;
    },
    async removeItem(it){
      if (!this.current) return;
      if (!confirm('Remove this animal from the sheet?')) return;
      try {
        var r = await executeJson({ function:'deleteSlaughterItem', slaughterSheetId: this.current.id, itemId: it.id }, flocker);
        if (r.data && r.data.success) {
          this.current.items = this.current.items.filter(function(x){ return x.id !== it.id; });
        } else alert((r.data && r.data.message) || 'Failed to remove animal');
      } catch(e){ alert('Network error'); }
    },
    recalcDress(it){
      if (it.cold_cw_kg && it.liveweight_kg) it.dress_pct = Math.round(100 * it.cold_cw_kg / it.liveweight_kg * 100) / 100;
    },
    gradeFor(it){
      var age = Number(it.est_age_months), fat = Number(it.fat_score), mus = Number(it.muscle_score), dress = Number(it.dress_pct);
      if (!(age > 0) && !(fat > 0) && !(mus > 0) && !(dress > 0)) return '';
      // Points out of 10: age ≤24mo best (0-3), muscling 1-5 (0-3), fat cover ideal 2-3 (0-2), dressing % (0-2)
      var pts = 0;
      pts += (age > 0) ? (age <= 24 ? 3 : age <= 36 ? 2 : age <= 48 ? 1 : 0) : 1.5;
      pts += (mus > 0) ? (mus - 1) * 0.75 : 1.5;
      pts += (fat > 0) ? ((fat === 2 || fat === 3) ? 2 : (fat === 1 || fat === 4) ? 1 : 0) : 1;
      pts += (dress > 0) ? (dress >= 55 ? 2 : dress >= 50 ? 1.5 : dress >= 45 ? 1 : 0) : 1;
      return pts >= 7.5 ? 'A' : pts >= 5.5 ? 'B' : pts >= 3.5 ? 'C' : 'D';
    },
    recalcOffal(it){
      var sum = 0;
      ['hide_kg','liver_kg','tripe_kg','heart_kg','lungs_kg','kidneys_kg','head_kg','legs_kg','other_offal_kg'].forEach(function(f){
        sum += Number(it[f]) || 0;
      });
      it.total_offal_kg = Math.round(sum * 100) / 100;
    },
    sumOf(f){
      if (!this.current) return 0;
      var s = 0; this.current.items.forEach(function(i){ s += Number(i[f]) || 0; });
      return Math.round(s * 100) / 100;
    },
    avgOf(f){
      if (!this.current) return 0;
      var vals = this.current.items.map(function(i){ return Number(i[f]) || 0 }).filter(function(v){return v > 0});
      if (!vals.length) return 0;
      var s = vals.reduce(function(a,b){return a+b}, 0);
      return Math.round((s / vals.length) * 100) / 100;
    },
    async saveAll(){
      if (!this.current) return;
      this.saving = true;
      try {
        var headerFields = ['abattoir_name','abattoir_reg_no','abattoir_licence_no','slaughter_date','vet_officer_name','vet_dvs_no',
                            'arrival_at','lairage_hours','transporter_name','vehicle_reg','dvs_permit_no','ante_mortem_remarks','pm_remarks',
                            'meat_inspector_name','meat_inspector_reg_no','animals_passed','animals_condemned','total_weight_condemned_kg',
                            'notifiable_disease','dvs_notification_ref','ranch_signed_name','abattoir_signed_name','inspector_signed_name','buyer_signed_name','notes'];
        var hp = { function:'updateSlaughterSheet', slaughterSheetId: this.current.id };
        var self = this;
        headerFields.forEach(function(f){ if (self.current[f] !== undefined) hp[f] = self.current[f] === null ? '' : String(self.current[f]); });
        await executeJson(hp, flocker);

        var bp = {
          function: 'bulkUpdateSlaughterItems',
          slaughterSheetId: this.current.id,
          items: JSON.stringify(this.current.items.map(function(it){
            return {
              id: it.id, ear_tag: it.ear_tag, animal_class: it.animal_class, breed_type: it.breed_type, liveweight_kg: it.liveweight_kg,
              ante_mortem_status: it.ante_mortem_status, slaughter_time: it.slaughter_time,
              hot_cw_kg: it.hot_cw_kg, cold_cw_kg: it.cold_cw_kg, dress_pct: it.dress_pct,
              side_l_kg: it.side_l_kg, side_r_kg: it.side_r_kg, grade: self.gradeFor(it) || it.grade,
              fat_score: it.fat_score, muscle_score: it.muscle_score, bruise_location: it.bruise_location, trim_loss_kg: it.trim_loss_kg,
              hide_kg: it.hide_kg, liver_kg: it.liver_kg, tripe_kg: it.tripe_kg, heart_kg: it.heart_kg, lungs_kg: it.lungs_kg, kidneys_kg: it.kidneys_kg,
              head_kg: it.head_kg, legs_kg: it.legs_kg, blood_l: it.blood_l, other_offal_kg: it.other_offal_kg, total_offal_kg: it.total_offal_kg,
              pm_status: it.pm_status, liver_condition: it.liver_condition, lungs_condition: it.lungs_condition,
              lymph_nodes_condition: it.lymph_nodes_condition, condemned_parts: it.condemned_parts, condemn_reason: it.condemn_reason,
              vet_initials: it.vet_initials, price_per_kg_kes: it.price_per_kg_kes, amount_kes: it.amount_kes, remarks: it.remarks,
              est_age_months: it.est_age_months
            };
          }))
        };
        await executeJson(bp, flocker);
      } catch(e){ alert('Network error') }
      this.saving = false;
    },
    async signOff(){
      if (!confirm('Sign off and lock this sheet? Auto-tallies passed and condemned counts.')) return;
      await this.saveAll();
      try {
        var r = await executeJson({ function:'signOffSlaughterSheet', slaughterSheetId: this.current.id }, flocker);
        if (r.data && r.data.success) {
          alert('Signed off — passed ' + r.data.data.passed + ', condemned ' + r.data.data.condemned);
          this.cancelEdit();
        }
      } catch(e){ alert('Network error') }
    },
    openPrint(s){
      var route = this.$router.resolve({ path: '/slaughter-sheet/' + s.id + '/print' });
      window.open(route.href, '_blank');
    },
    // ----- Generate Invoice flow -----
    async openInvoiceModal(){
      this.invoiceModalOpen = true;
      this.selectedClientId = '';
      this.invoicePreview = null;
      this.poNumber = '';
      this.invoiceNotes = '';
      if (!this.corporateClients.length) await this.loadCorporateClients();
    },
    closeInvoiceModal(){ this.invoiceModalOpen = false; },
    async loadCorporateClients(){
      this.loadingClients = true;
      try {
        var r = await executeJson({ function:'getMyCorporateClients', sellerId: sessionStorage.getItem('userId') }, flocker);
        if (r.data && r.data.success) {
          this.corporateClients = (r.data.data.clients || []).filter(function(c){ return c.status === 'active'; });
        }
      } catch(e){}
      this.loadingClients = false;
    },
    async loadPreview(){
      this.invoicePreview = null;
      if (!this.selectedClientId) return;
      this.loadingPreview = true;
      try {
        var ids = this.invoiceableItems.map(function(it){ return it.id; });
        var r = await executeJson({
          function:'previewCorporateInvoice',
          sellerId: sessionStorage.getItem('userId'),
          clientId: this.selectedClientId,
          slaughterItemIds: JSON.stringify(ids)
        }, flocker);
        if (r.data && r.data.success) this.invoicePreview = r.data.data;
        else alert((r.data && r.data.message) || 'Preview failed');
      } catch(e){ alert('Preview failed: ' + e.message); }
      this.loadingPreview = false;
    },
    async confirmGenerate(){
      if (!this.selectedClientId || !this.invoicePreview) return;
      if (!confirm('Issue invoice for KES ' + Number(this.invoicePreview.total).toLocaleString() + ' to ' + (this.selectedClient && this.selectedClient.company_name) + '? They will be notified by SMS + email.')) return;
      this.generating = true;
      try {
        var ids = this.invoiceableItems.map(function(it){ return it.id; });
        var r = await executeJson({
          function:'generateCorporateInvoice',
          sellerId: sessionStorage.getItem('userId'),
          clientId: this.selectedClientId,
          slaughterItemIds: JSON.stringify(ids),
          purchaseOrderNumber: this.poNumber,
          notes: this.invoiceNotes
        }, flocker);
        if (r.data && r.data.success) {
          this.invoiceModalOpen = false;
          alert('Invoice ' + r.data.data.invoiceNumber + ' issued. ' + (this.selectedClient && this.selectedClient.company_name) + ' has been notified.');
          this.$router.push('/receivables/invoice/' + r.data.data.invoiceId);
        } else alert((r.data && r.data.message) || 'Generation failed');
      } catch(e){ alert('Generation failed: ' + e.message); }
      this.generating = false;
    },
    basisLabel(b){ return ({ hot:'hot carcass', cold:'cold carcass', live:'live weight' })[b] || b; },
    formatMethod(m){ return (m || '').replace(/_/g, ' '); }
  }
};
</script>

<style scoped>
/* ===== Generate-invoice modal ===== */
.ss-modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:flex-start;justify-content:center;z-index:2000;overflow-y:auto;padding:40px 16px}
.ss-modal{background:#FFFFFF;width:100%;max-width:720px;border-radius:14px;padding:22px;box-shadow:0 12px 36px rgba(0,0,0,.25);color:#1A1A1A}
html[data-theme="dark"] .ss-modal{background:#141414;color:#F5F0E8;border:1px solid #2A2520}
.ss-modal-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;padding-bottom:10px;border-bottom:1px solid #E8E2DA}
html[data-theme="dark"] .ss-modal-head{border-color:#2A2520}
.ss-modal-head h5{margin:0}
.ss-close{background:none;border:none;font-size:22px;color:#6B6356;cursor:pointer}
.ss-empty{text-align:center;padding:30px 10px;color:#6B6356}
.ss-empty i{font-size:48px;color:#BFA164;opacity:.6}
.ss-empty p{margin:8px 0 14px}
.ss-field{margin-bottom:12px}
.ss-field label{display:block;font-size:11px;color:#6B6356;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px;font-weight:600}
.ss-client-snap{display:flex;flex-wrap:wrap;gap:14px;padding:10px 14px;background:#FBF6E8;border-left:3px solid #BFA164;border-radius:0 8px 8px 0;font-size:12px;margin-bottom:14px}
html[data-theme="dark"] .ss-client-snap{background:rgba(191,161,100,.08)}
.ss-client-snap span{color:#5C4533}
html[data-theme="dark"] .ss-client-snap span{color:#D8CDB6}
.ss-loading{text-align:center;padding:20px;color:#8A827A;font-size:13px}
.ss-preview h6{font-size:12px;color:#8B5A2B;text-transform:uppercase;letter-spacing:1px;margin:8px 0}
.ss-preview-tbl{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:12px}
.ss-preview-tbl th{background:#F5EEDE;color:#5C4533;padding:7px 9px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #D8C9A3}
html[data-theme="dark"] .ss-preview-tbl th{background:#1F1B14;color:#D8CDB6;border-color:#2A2520}
.ss-preview-tbl td{padding:6px 9px;border-bottom:1px solid #F0EBE4}
html[data-theme="dark"] .ss-preview-tbl td{border-color:#2A2520}
.ss-preview-tbl th.num,.ss-preview-tbl td.num{text-align:right}
.ss-preview-tbl tfoot td{font-weight:600;border-top:1px solid #D8C9A3;border-bottom:none;padding-top:8px}
.ss-preview-tbl tfoot tr.total-row td{font-size:14px;background:#FBF6E8;padding:9px}
html[data-theme="dark"] .ss-preview-tbl tfoot tr.total-row td{background:rgba(191,161,100,.1)}
.ss-cond{color:#C45A2C;font-weight:700;margin-left:2px;cursor:help}
.ss-modal-foot{display:flex;justify-content:flex-end;gap:8px;margin-top:16px;padding-top:14px;border-top:1px solid #E8E2DA}
html[data-theme="dark"] .ss-modal-foot{border-color:#2A2520}
@media(max-width:640px){
  .ss-client-snap{flex-direction:column;gap:4px}
  .ss-preview-tbl{font-size:11px}
}
</style>
